import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import SectionHeader from "./SectionHeader";
import SignedImage from "./SignedImage";
import useSignedUrl from "../../utils/useSignedUrl";
import usePublicTeamContent from "../../utils/usePublicTeamContent";
import { normalizeStoryRouteTarget, storyRouteLinkProps } from "./ctaTargets";
import { COLORS } from "../../theme/tokens";

const STORY_BADGE_SX = {
  height: 30,
  fontWeight: 700,
  "& .MuiChip-label": { px: 1.25 },
};

function DocumentLink({ label, s3Key }) {
  const { url } = useSignedUrl(s3Key);
  if (!s3Key) return null;

  if (url) {
    return (
      <Button
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        size="small"
        sx={{ justifyContent: "flex-start", color: COLORS.red, m: 0, textTransform: "none" }}
      >
        {label}
      </Button>
    );
  }
  return (
    <Button
      component={Link}
      to="/documenten"
      size="small"
      sx={{ justifyContent: "flex-start", color: COLORS.red, m: 0, textTransform: "none" }}
    >
      {label} (via documenten)
    </Button>
  );
}

function StoryCard({ block }) {
  const c = block.content || {};
  const isHealthySchoolSummary =
    c.pill === "Gezonde school" || c.sourceRoute === "/gezondeschool";
  const isStichtSummary = c.pill === "Het Sticht" || c.sourceRoute === "/sticht";
  let ctaTarget = c.ctaRoute;
  let ctaLabel = c.ctaLabel;
  if (isStichtSummary) {
    ctaTarget = "/sticht";
    ctaLabel = "Meer over Het Sticht";
  } else if (isHealthySchoolSummary) {
    ctaTarget = "/visie";
    ctaLabel = "Lees over welbevinden";
  } else if (c.sourceRoute && (!c.ctaRoute || c.ctaRoute === "#school")) {
    ctaTarget = c.sourceRoute;
  }
  return (
    <Stack
      spacing={0}
      sx={{
        backgroundColor: "#fff",
        borderRadius: 1,
        overflow: "hidden",
        height: "100%",
        border: "1px solid rgba(32, 48, 51, 0.14)",
        boxShadow: "0 16px 36px rgba(32, 48, 51, 0.1)",
      }}
    >
      {c.s3Key && (
        <Box sx={{ aspectRatio: "4 / 3", overflow: "hidden" }}>
          <SignedImage s3Key={c.s3Key} alt={c.imageAlt || c.heading} />
        </Box>
      )}
      <Stack spacing={1.5} sx={{ p: 3, flex: 1 }}>
        {c.meta && (
          <Typography
            sx={{
              color: COLORS.muted,
              fontWeight: 700,
              fontSize: "0.92rem",
            }}
          >
            {c.meta}
          </Typography>
        )}
        <Typography
          variant="h4"
          component="h3"
          sx={{ fontFamily: "windroos, sans-serif", color: COLORS.blue, p: 0, m: 0 }}
        >
          {c.heading}
        </Typography>
        <Typography variant="body2" sx={{ color: COLORS.muted }}>
          {c.body}
        </Typography>
        {ctaLabel && ctaTarget && (
          <Box sx={{ mt: "auto", pt: 1 }}>
            <Button
              component={Link}
              {...storyRouteLinkProps(ctaTarget)}
              size="small"
              sx={{ color: COLORS.red, m: 0, px: 0, minHeight: 24, py: 0.5 }}
            >
              {ctaLabel}{"\u00a0→"}
            </Button>
          </Box>
        )}
      </Stack>
    </Stack>
  );
}

function StackItem({ block }) {
  const c = block.content || {};
  const normalizedExternalUrl = normalizeStoryRouteTarget(c.externalUrl);
  const isHealthySchoolSummary =
    c.pill === "Gezonde school" || c.sourceRoute === "/gezondeschool";
  const isStichtSummary = c.pill === "Het Sticht" || c.sourceRoute === "/sticht";
  let ctaTarget = c.ctaRoute;
  let ctaLabel = c.ctaLabel;
  if (isStichtSummary) {
    ctaTarget = "/sticht";
    ctaLabel = "Meer over Het Sticht";
  } else if (isHealthySchoolSummary) {
    ctaTarget = "/visie";
    ctaLabel = "Lees over welbevinden";
  }
  return (
    <Stack
      spacing={1.25}
      sx={{
        backgroundColor: "#fff",
        borderRadius: 1,
        p: 2.75,
        border: "1px solid rgba(32, 48, 51, 0.14)",
        boxShadow: "0 16px 36px rgba(32, 48, 51, 0.08)",
      }}
    >
      {c.pill && (
        <Chip
          label={c.pill}
          sx={{
            ...STORY_BADGE_SX,
            alignSelf: "flex-start",
            backgroundColor: "rgba(136, 205, 211, 0.24)",
            color: "#2B6870",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontSize: "0.78rem",
          }}
        />
      )}
      <Typography
        variant="h5"
        component="h3"
        sx={{ fontFamily: "windroos, sans-serif", color: COLORS.blue, m: 0 }}
      >
        {c.heading}
      </Typography>
      <Typography variant="body2" sx={{ color: COLORS.muted }}>
        {c.body}
      </Typography>

      {!isStichtSummary && Array.isArray(c.documenten) && c.documenten.length > 0 && (
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          <Divider />
          {c.documenten.map((doc) => (
            <DocumentLink key={doc.s3Key} label={doc.label} s3Key={doc.s3Key} />
          ))}
        </Stack>
      )}

      {!isHealthySchoolSummary && c.documentS3Key && c.documentLabel && (
        <Box>
          <DocumentLink label={c.documentLabel} s3Key={c.documentS3Key} />
        </Box>
      )}

      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", pt: 0.5 }}>
        {ctaLabel && ctaTarget && (
          <Button
            component={Link}
            {...storyRouteLinkProps(ctaTarget)}
            size="small"
            sx={{ color: COLORS.red, m: 0, px: 0, minHeight: 24, py: 0.5 }}
          >
            {ctaLabel}{"\u00a0→"}
          </Button>
        )}
        {!isHealthySchoolSummary &&
          c.externalUrl &&
          normalizeStoryRouteTarget(c.ctaRoute) !== normalizedExternalUrl && (
            <Button
              component="a"
              href={normalizedExternalUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{ color: COLORS.blue, m: 0, px: 0 }}
            >
              Externe link ↗
            </Button>
          )}
        {!isStichtSummary && c.klachtenUrl && (
          <Button
            component="a"
            href={c.klachtenUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            sx={{ color: COLORS.blue, m: 0, px: 0 }}
          >
            Klachten ↗
          </Button>
        )}
        {!isStichtSummary && c.privacyUrl && (
          <Button
            component="a"
            href={c.privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            sx={{ color: COLORS.blue, m: 0, px: 0 }}
          >
            Privacy ↗
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

export default function FeatureSplitSection({ section }) {
  const { content, blocks = [] } = section;
  const teamContent = usePublicTeamContent();
  const sorted = [...blocks].sort((a, b) => a.order - b.order);
  let primary = sorted.find((b) => b.blockType === "media-card");
  let stackItems = sorted.filter((b) => b.blockType === "text-card");

  if (!primary && stackItems.length > 0) {
    [primary, ...stackItems] = stackItems;
  }

  if (primary?.content?.sourceRoute === "/team") {
    primary = {
      ...primary,
      content: {
        ...primary.content,
        s3Key: teamContent.imageS3Key,
        imageAlt: teamContent.imageAlt,
      },
    };
  }

  return (
    <Box
      id="school"
      component="section"
      sx={{
        py: "clamp(48px, 7vw, 92px)",
        px: "clamp(20px, 5vw, 72px)",
        backgroundColor: "#FFFAF4",
        scrollMarginTop: { xs: 64, md: 80 },
      }}
    >
      <Container disableGutters sx={{ maxWidth: { md: 1160 } }} maxWidth={false}>
        <SectionHeader
          eyebrow={content.eyebrow}
          heading={content.heading}
          lead={content.lead}
          reviewStatus="controleer"
        />
        <Box
          sx={{
            mt: 4,
            display: "grid",
            gap: "22px",
            gridTemplateColumns: { xs: "1fr", md: "1.2fr 1fr" },
            alignItems: "stretch",
          }}
        >
          {primary && <StoryCard block={primary} />}
          <Stack spacing={2}>
            {stackItems.map((block) => (
              <StackItem key={block.id} block={block} />
            ))}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
